import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Avatar,
    Badge,
    Box,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import server from '../../global/server';
import useHandleErrors from '../../hooks/useHandleErrors';

const hora = valor => new Date(valor).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const dia = valor =>
    new Date(valor).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });

/**
 * Canal interno entre Anfitrião e Conviva (Termos 1.11).
 *
 * O item 1.11 manda toda conversa sobre um Encontro já agendado acontecer
 * dentro da Plataforma e proíbe a Empiria repassar telefone, e-mail ou rede
 * social de um Usuário ao outro — por isso a tela mostra do outro lado apenas
 * nome, foto e @: é tudo que a outra pessoa precisa saber para conversar.
 *
 * A mesma tela serve aos dois papéis. Quem anfitriona e também compra Encontros
 * vê as duas coisas na mesma lista, cada linha dizendo de que lado ela está.
 */
const MeetingChat = () => {
    const { socket } = useApp();
    const navigation = useNavigate();
    const { id } = useParams();

    const [conversas, setConversas] = useState([]);
    const [aberta, setAberta] = useState(null);
    const [mensagens, setMensagens] = useState([]);
    const [texto, setTexto] = useState('');
    const [carregandoLista, setCarregandoLista] = useState(true);
    const [carregandoConversa, setCarregandoConversa] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState();
    useHandleErrors(erro);

    const fimDaLista = useRef(null);

    const carregarLista = useCallback(async () => {
        try {
            const { data } = await server.get('/conversas');
            setConversas(data.data || []);
        } catch (falha) {
            setErro(falha);
        } finally {
            setCarregandoLista(false);
        }
    }, []);

    useEffect(() => {
        carregarLista();
    }, [carregarLista]);

    useEffect(() => {
        if (!id) {
            setAberta(null);
            setMensagens([]);
            return;
        }

        let valido = true;
        setCarregandoConversa(true);

        server
            .get(`/conversas/${id}`)
            .then(({ data }) => {
                if (!valido) return;
                setAberta(data.data?.chat || null);
                setMensagens(data.data?.messages || []);
                // Abrir zerou o não lido: a lista precisa refletir isso sem
                // esperar a próxima visita à tela
                setConversas(atual =>
                    atual.map(item => (item.id === Number(id) ? { ...item, nao_lidas: 0 } : item)),
                );
            })
            .catch(falha => valido && setErro(falha))
            .finally(() => valido && setCarregandoConversa(false));

        return () => {
            valido = false;
        };
    }, [id]);

    useEffect(() => {
        fimDaLista.current?.scrollIntoView({ behavior: 'smooth' });
    }, [mensagens]);

    // O servidor precisa saber qual conversa esta na frente da pessoa: com ela
    // aberta, a mensagem chega na tela e o aviso no sininho apontaria para onde
    // ela ja esta. O aviso e reenviado a cada reconexao porque a presenca vive
    // na conexao: quem cai perde a sala.
    useEffect(() => {
        if (!socket || !id) return;

        const avisarQueEstaAberta = () => socket.emit('conversa:abrir', Number(id));

        avisarQueEstaAberta();
        socket.on('connect', avisarQueEstaAberta);

        return () => {
            socket.off('connect', avisarQueEstaAberta);
            socket.emit('conversa:fechar', Number(id));
        };
    }, [socket, id]);

    useEffect(() => {
        if (!socket) return;

        const aoReceber = ({ chat_id, message }) => {
            // A mensagem chega para os dois lados. Se a conversa está aberta,
            // entra na hora; se não, muda só a prévia e o contador da lista.
            if (Number(chat_id) === Number(id)) {
                setMensagens(atual =>
                    atual.some(item => item.id === message.id) ? atual : [...atual, message],
                );
            }

            setConversas(atual =>
                atual.map(item =>
                    item.id === chat_id
                        ? {
                              ...item,
                              lastMessage: message.body,
                              lastMessageAt: message.createdAt,
                              nao_lidas:
                                  Number(chat_id) === Number(id) ? 0 : (item.nao_lidas || 0) + 1,
                          }
                        : item,
                ),
            );
        };

        socket.on('conversa:mensagem', aoReceber);
        return () => socket.off('conversa:mensagem', aoReceber);
    }, [socket, id]);

    const enviar = async () => {
        const corpo = texto.trim();
        if (!corpo || enviando || !aberta?.aberta) return;

        try {
            setEnviando(true);
            const { data } = await server.post(`/conversas/${id}/mensagem`, { body: corpo });
            setTexto('');
            setMensagens(atual =>
                atual.some(item => item.id === data.data.id) ? atual : [...atual, data.data],
            );
        } catch (falha) {
            setErro(falha);
        } finally {
            setEnviando(false);
        }
    };

    const ordenadas = useMemo(
        () =>
            [...conversas].sort(
                (a, b) => new Date(b.lastMessageAt || b.createdAt) - new Date(a.lastMessageAt || a.createdAt),
            ),
        [conversas],
    );

    // Numa tela estreita não cabem os dois painéis: com uma conversa aberta o
    // celular mostra a conversa, e a seta de voltar leva de novo à lista.
    const listaVisivel = { xs: id ? 'none' : 'block', md: 'block' };
    const conversaVisivel = { xs: id ? 'block' : 'none', md: 'block' };

    return (
        <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', px: { xs: '12px', sm: '16px' }, py: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Conversas
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
                <Paper
                    variant="outlined"
                    sx={{
                        display: listaVisivel,
                        width: { xs: '100%', md: 320 },
                        flexShrink: 0,
                        borderRadius: '16px',
                        overflow: 'hidden',
                    }}>
                    {carregandoLista ? (
                        <Stack alignItems="center" sx={{ py: 4 }}>
                            <CircularProgress size={24} />
                        </Stack>
                    ) : ordenadas.length === 0 ? (
                        <Stack alignItems="center" spacing={1} sx={{ py: 5, px: 3 }}>
                            <ChatBubbleOutlineIcon sx={{ color: '#bdbdbd' }} />
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                Nenhuma conversa ainda. Elas começam pela página do encontro que você
                                comprou ou promoveu.
                            </Typography>
                        </Stack>
                    ) : (
                        <Stack divider={<Divider />}>
                            {ordenadas.map(conversa => (
                                <Box
                                    key={conversa.id}
                                    onClick={() => navigation(`/conversas/${conversa.id}`)}
                                    sx={{
                                        display: 'flex',
                                        gap: 1.5,
                                        alignItems: 'center',
                                        px: 2,
                                        py: 1.5,
                                        cursor: 'pointer',
                                        bgcolor: Number(id) === conversa.id ? '#f0f7f6' : 'transparent',
                                        '&:hover': { bgcolor: '#fafafa' },
                                    }}>
                                    <Badge color="error" badgeContent={conversa.nao_lidas || 0}>
                                        <Avatar src={conversa.outro?.profileImage} sx={{ width: 40, height: 40 }}>
                                            {conversa.outro?.name?.[0]}
                                        </Avatar>
                                    </Badge>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="body2" fontWeight={600} noWrap>
                                            {conversa.outro?.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                                            {conversa.encontro?.titulo}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                                            {conversa.lastMessage || 'Sem mensagens'}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Paper>

                <Box sx={{ display: conversaVisivel, flex: 1, minWidth: 0 }}>
                    {!id ? (
                        <Paper
                            variant="outlined"
                            sx={{
                                borderRadius: '16px',
                                height: '60vh',
                                display: { xs: 'none', md: 'flex' },
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                            <Typography variant="body2" color="text.secondary">
                                Escolha uma conversa ao lado.
                            </Typography>
                        </Paper>
                    ) : carregandoConversa ? (
                        <Stack alignItems="center" sx={{ py: 6 }}>
                            <CircularProgress size={24} />
                        </Stack>
                    ) : (
                        aberta && (
                            <Stack spacing={1.5}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <IconButton
                                        onClick={() => navigation('/conversas')}
                                        sx={{ display: { xs: 'inline-flex', md: 'none' } }}>
                                        <ArrowBackIcon />
                                    </IconButton>
                                    <Avatar src={aberta.outro?.profileImage} sx={{ width: 40, height: 40 }}>
                                        {aberta.outro?.name?.[0]}
                                    </Avatar>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="body2" fontWeight={700} noWrap>
                                            {aberta.outro?.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                                            {aberta.encontro?.titulo}
                                            {aberta.encontro?.datetime ? ` · ${dia(aberta.encontro.datetime)}` : ''}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        size="small"
                                        label={aberta.meu_papel === 'ANFITRIAO' ? 'Você anfitriona' : 'Você é conviva'}
                                    />
                                </Stack>

                                {!aberta.aberta && (
                                    <Alert severity="info" sx={{ borderRadius: '12px' }}>
                                        Esta conversa foi encerrada e ficou disponível apenas para consulta.
                                        Se ainda precisar de algo sobre este encontro, fale com a equipe pelo
                                        atendimento.
                                    </Alert>
                                )}

                                <Paper
                                    variant="outlined"
                                    sx={{
                                        borderRadius: '16px',
                                        p: 2,
                                        height: '52vh',
                                        overflowY: 'auto',
                                        bgcolor: '#fafafa',
                                    }}>
                                    {mensagens.length === 0 ? (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            textAlign="center"
                                            sx={{ py: 4 }}>
                                            Nenhuma mensagem ainda. Trate por aqui o que for do encontro —
                                            combinados, ajustes e problemas técnicos.
                                        </Typography>
                                    ) : (
                                        <Stack spacing={1}>
                                            {mensagens.map(mensagem => {
                                                const meu = mensagem.sender === aberta.meu_papel;
                                                return (
                                                    <Box
                                                        key={mensagem.id}
                                                        sx={{
                                                            alignSelf: meu ? 'flex-end' : 'flex-start',
                                                            maxWidth: '80%',
                                                            bgcolor: meu ? '#006a61' : 'white',
                                                            color: meu ? 'white' : 'text.primary',
                                                            border: meu ? 'none' : '1px solid #e8e8e8',
                                                            borderRadius: '12px',
                                                            px: 1.5,
                                                            py: 1,
                                                        }}>
                                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                            {mensagem.body}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{ opacity: 0.7, display: 'block', textAlign: 'right' }}>
                                                            {hora(mensagem.createdAt)}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                            <div ref={fimDaLista} />
                                        </Stack>
                                    )}
                                </Paper>

                                {aberta.aberta && (
                                    <Stack direction="row" spacing={1}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            multiline
                                            maxRows={4}
                                            placeholder="Escreva a sua mensagem"
                                            value={texto}
                                            onChange={evento => setTexto(evento.target.value)}
                                            onKeyDown={evento => {
                                                // Enter envia, Shift+Enter quebra linha
                                                if (evento.key === 'Enter' && !evento.shiftKey) {
                                                    evento.preventDefault();
                                                    enviar();
                                                }
                                            }}
                                        />
                                        <IconButton
                                            color="primary"
                                            disabled={enviando || !texto.trim()}
                                            onClick={enviar}>
                                            {enviando ? <CircularProgress size={20} /> : <SendIcon />}
                                        </IconButton>
                                    </Stack>
                                )}

                                <Typography variant="caption" color="text.secondary">
                                    Trate do encontro por aqui. A Empiria não compartilha telefone, e-mail
                                    nem redes sociais entre usuários, e combinar fora da Plataforma é
                                    vedado pelos Termos.
                                </Typography>
                            </Stack>
                        )
                    )}
                </Box>
            </Stack>
        </Box>
    );
};

export default MeetingChat;
